// frontend/src/widgets/CameraIngestActivityWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-20
// Updated:      2026-09-23 17:57 BST
//
// Description:
//   Camera ingest activity widget.
//   Responsibilities:
//     - Show recent ingest events across cameras
//     - Surface parse status / errors
//     - Show decode / inserted / duplicate counts
//     - Provide quick operational visibility into ingest health
//
// Mobile Responsiveness (2026-09-07):
//   - Reflows the five summary counters into a compact mobile grid.
//   - Uses the existing table/row DOM as mobile ingest-event cards; no duplicate
//     list component or second data path is mounted.
//   - Measures the first rendered event card so one complete event record is
//     visible in the mobile list viewport before scrolling to the next event.
//   - Keeps all eight event fields visible on mobile and allows long parse
//     errors to wrap without truncating the operational information.
//   - Expands the nearest ReactGridLayout host to the widget's natural mobile
//     height because mobile widgets do not expose the desktop resize handle.
//
// Live Activity Upgrade (2026-09-23):
//   - Replaces the old 25 / 50 / 100 row selector with operational filters:
//       • tenant camera
//       • last 5 / 10 / 15 / 30 / 60 minutes
//       • all / parsed / received / errors
//   - Polls the filtered activity endpoint every 5 seconds.
//   - Loads camera filter options from the existing tenant camera endpoint.
//   - Summary counters are calculated from the currently filtered event set.
//   - Displays live state, last refresh time, visible event count and a warning
//     if the backend result safety ceiling is reached.
//   - Keeps manual Refresh and the existing desktop/mobile event presentation.
//
// Notes:
//   - Uses /api/anpr/cameras/ingest-events/recent
//   - Uses /api/anpr/cameras for the tenant camera filter
//   - Polls every 5 seconds
//   - Designed as an operational read widget, not an admin widget

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const TIME_WINDOW_OPTIONS = [5, 10, 15, 30, 60];
const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "parsed", label: "Parsed" },
  { value: "received", label: "Received" },
  { value: "error", label: "Errors" },
];

const LIVE_POLL_INTERVAL_MS = 5000;
const ACTIVITY_RESULT_LIMIT = 2000;
const MOBILE_BREAKPOINT = 768;
const MOBILE_CARD_FALLBACK_HEIGHT = 480;

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const formatTimeOnly = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "—";
  }
};

const getStatusPillStyle = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "parsed") {
    return {
      backgroundColor: "#ecfdf5",
      border: "1px solid #a7f3d0",
      color: "#065f46",
    };
  }

  if (normalized === "received") {
    return {
      backgroundColor: "#eff6ff",
      border: "1px solid #bfdbfe",
      color: "#1d4ed8",
    };
  }

  return {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
  };
};

const mobileLabelStyle = {
  fontSize: "0.72rem",
  fontWeight: 800,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.035em",
  lineHeight: 1.25,
};

const CameraIngestActivityWidget = () => {
  const { theme } = useTheme();

  const [rows, setRows] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [minutes, setMinutes] = useState(15);
  const [cameraDeviceId, setCameraDeviceId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  const [mobileCardViewportHeight, setMobileCardViewportHeight] = useState(null);

  const rootRef = useRef(null);
  const firstEventRowRef = useRef(null);
  const requestSequenceRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const loadCameras = async () => {
      setCameraError("");

      try {
        const res = await axiosInstance.get("anpr/cameras");
        if (cancelled) return;

        setCameras(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (cancelled) return;

        console.error("Failed to load camera filter options", err);
        setCameraError(
          err?.response?.data?.detail ||
            "Camera filter options could not be loaded."
        );
      }
    };

    loadCameras();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++requestSequenceRef.current;

      if (!silent) setLoading(true);
      setRefreshing(true);
      setError("");

      const params = {
        minutes,
        limit: ACTIVITY_RESULT_LIMIT,
      };

      if (cameraDeviceId) {
        params.camera_device_id = Number(cameraDeviceId);
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      try {
        const res = await axiosInstance.get(
          "anpr/cameras/ingest-events/recent",
          { params }
        );

        if (requestId !== requestSequenceRef.current) return;

        setRows(Array.isArray(res.data) ? res.data : []);
        setLastUpdatedAt(new Date());
      } catch (err) {
        if (requestId !== requestSequenceRef.current) return;

        console.error("Failed to load camera ingest activity", err);
        setError(
          err?.response?.data?.detail ||
            "Failed to load recent ingest activity."
        );
      } finally {
        if (requestId === requestSequenceRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [minutes, cameraDeviceId, statusFilter]
  );

  useEffect(() => {
    loadData();

    const timer = window.setInterval(() => {
      loadData({ silent: true });
    }, LIVE_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [loadData]);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  const summary = useMemo(() => {
    const counts = {
      parsed: 0,
      received: 0,
      error: 0,
      inserted: 0,
      duplicates: 0,
    };

    rows.forEach((row) => {
      const status = String(row.parse_status || "").toLowerCase();
      if (status === "parsed") counts.parsed += 1;
      else if (status === "received") counts.received += 1;
      else counts.error += 1;

      counts.inserted += Number(row.inserted_count || 0);
      counts.duplicates += Number(row.duplicate_count || 0);
    });

    return counts;
  }, [rows]);

  const cameraOptions = useMemo(() => {
    return [...cameras].sort((a, b) => {
      const aName = String(a?.camera_name || "").trim().toLowerCase();
      const bName = String(b?.camera_name || "").trim().toLowerCase();

      if (aName && bName && aName !== bName) {
        return aName.localeCompare(bName);
      }

      if (aName && !bName) return -1;
      if (!aName && bName) return 1;

      return Number(a?.id || 0) - Number(b?.id || 0);
    });
  }, [cameras]);

  const resultLimitReached = rows.length >= ACTIVITY_RESULT_LIMIT;
  const displayError = [cameraError, error].filter(Boolean).join(" ");

  useEffect(() => {
    if (!isMobile || rows.length === 0) {
      setMobileCardViewportHeight(null);
      return undefined;
    }

    let frameId = null;
    let observer = null;

    const measureFirstEventCard = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        const row = firstEventRowRef.current;
        if (!row) return;

        const height = Math.ceil(row.getBoundingClientRect().height);
        if (height > 0) {
          setMobileCardViewportHeight(height);
        }
      });
    };

    measureFirstEventCard();
    window.addEventListener("resize", measureFirstEventCard);

    if (typeof ResizeObserver !== "undefined" && firstEventRowRef.current) {
      observer = new ResizeObserver(measureFirstEventCard);
      observer.observe(firstEventRowRef.current);
    }

    return () => {
      window.removeEventListener("resize", measureFirstEventCard);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isMobile, rows]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (!isMobile) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, [
    isMobile,
    mobileCardViewportHeight,
    rows.length,
    error,
    refreshing,
  ]);

  if (loading && rows.length === 0) {
    return (
      <div style={{ padding: "8px 4px", color: "#444" }}>
        Loading ingest activity...
      </div>
    );
  }

  const summaryItems = [
    {
      label: "Parsed",
      value: summary.parsed,
      style: {
        border: "1px solid #dbeafe",
        backgroundColor: "#eff6ff",
        color: "#1d4ed8",
      },
    },
    {
      label: "Received",
      value: summary.received,
      style: {
        border: "1px solid #bfdbfe",
        backgroundColor: "#eff6ff",
        color: "#1d4ed8",
      },
    },
    {
      label: "Errors",
      value: summary.error,
      style: {
        border: "1px solid #fecaca",
        backgroundColor: "#fef2f2",
        color: "#991b1b",
      },
    },
    {
      label: "Inserted",
      value: summary.inserted,
      style: {
        border: "1px solid #a7f3d0",
        backgroundColor: "#ecfdf5",
        color: "#065f46",
      },
    },
    {
      label: "Duplicates",
      value: summary.duplicates,
      style: {
        border: "1px solid #fde68a",
        backgroundColor: "#fffbeb",
        color: "#92400e",
      },
    },
  ];

  const refreshButtonStyle = {
    backgroundColor: theme.button_action?.normal || "#000",
    color: "#fff",
    padding: "8px 14px",
    borderRadius: "8px",
    border: "none",
    fontWeight: 700,
    fontFamily: "var(--font-body)",
    cursor: refreshing ? "not-allowed" : "pointer",
    opacity: refreshing ? 0.7 : 1,
    ...(isMobile
      ? {
          width: "100%",
          minHeight: "44px",
          fontSize: "0.95rem",
        }
      : {}),
  };

  const filterLabelStyle = {
    display: "block",
    fontWeight: 700,
    color: "#333",
    marginBottom: "5px",
    fontSize: "0.86rem",
  };

  const filterSelectStyle = {
    width: "100%",
    minHeight: isMobile ? "44px" : "40px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    padding: "8px 10px",
    backgroundColor: "#fff",
    fontFamily: "var(--font-body)",
    fontSize: isMobile ? "16px" : "0.92rem",
  };

  const mobileCellStyle = (isLast = false) =>
    isMobile
      ? {
          display: "grid",
          gridTemplateColumns: "96px minmax(0, 1fr)",
          gap: "10px",
          alignItems: "start",
          padding: "10px 12px",
          borderBottom: isLast ? "none" : "1px solid #efefef",
          minWidth: 0,
          whiteSpace: "normal",
        }
      : {
          padding: "10px 12px",
        };

  const mobileCardHeight =
    mobileCardViewportHeight || MOBILE_CARD_FALLBACK_HEIGHT;

  return (
    <div
      ref={rootRef}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        height: isMobile ? "auto" : "100%",
        minHeight: 0,
        width: "100%",
        overflow: isMobile ? "visible" : undefined,
      }}
    >
      {displayError && (
        <div
          style={{
            backgroundColor: "#fff4f4",
            border: "1px solid #f0caca",
            color: "#a10000",
            borderRadius: "8px",
            padding: "10px 12px",
            fontSize: "0.95rem",
            overflowWrap: "anywhere",
          }}
        >
          {displayError}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "repeat(2, minmax(0, 1fr))"
            : "repeat(5, minmax(0, 1fr))",
          gap: isMobile ? "8px" : "12px",
        }}
      >
        {summaryItems.map((item, index) => (
          <div
            key={item.label}
            style={{
              borderRadius: "10px",
              padding: isMobile ? "10px" : "12px",
              minWidth: 0,
              ...(isMobile && index === summaryItems.length - 1
                ? { gridColumn: "1 / span 2" }
                : {}),
              ...item.style,
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "0.82rem" : "0.9rem",
                fontWeight: 700,
              }}
            >
              {item.label}
            </div>
            <div
              style={{
                fontSize: isMobile ? "1.35rem" : "1.5rem",
                fontWeight: 800,
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "minmax(180px, 1.4fr) minmax(130px, 0.8fr) minmax(150px, 0.9fr) auto",
          gap: "10px",
          alignItems: "end",
        }}
      >
        <div>
          <label style={filterLabelStyle}>Camera</label>
          <select
            value={cameraDeviceId}
            onChange={(e) => setCameraDeviceId(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="">All Cameras</option>
            {cameraOptions.map((camera) => (
              <option key={camera.id} value={camera.id}>
                {camera.camera_name || `Camera ${camera.id}`}
                {camera.serial_no ? ` · ${camera.serial_no}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={filterLabelStyle}>Time Window</label>
          <select
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
            style={filterSelectStyle}
          >
            {TIME_WINDOW_OPTIONS.map((option) => (
              <option key={option} value={option}>
                Last {option} mins
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={filterLabelStyle}>Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={filterSelectStyle}
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => loadData({ silent: true })}
          disabled={refreshing}
          style={refreshButtonStyle}
          onMouseEnter={(e) => {
            if (!refreshing) {
              e.currentTarget.style.backgroundColor =
                theme.button_action?.hover || "#333";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor =
              theme.button_action?.normal || "#000";
          }}
          onMouseDown={(e) => {
            if (!refreshing) {
              e.currentTarget.style.backgroundColor =
                theme.button_action?.click || "#111";
            }
          }}
          onMouseUp={(e) => {
            if (!refreshing) {
              e.currentTarget.style.backgroundColor =
                theme.button_action?.hover || "#333";
            }
          }}
        >
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between",
          gap: "8px",
          padding: "9px 11px",
          border: "1px solid #dcfce7",
          borderRadius: "9px",
          backgroundColor: "#f0fdf4",
          color: "#166534",
          fontSize: "0.86rem",
          fontWeight: 700,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: "9px",
              height: "9px",
              borderRadius: "999px",
              backgroundColor: "#16a34a",
              flexShrink: 0,
            }}
          />
          <span>Live · refreshes every 5 seconds</span>
        </div>

        <div
          style={{
            color: "#4b5563",
            fontWeight: 600,
            overflowWrap: "anywhere",
          }}
        >
          {rows.length.toLocaleString("en-GB")} events
          {" · "}
          Updated {formatTimeOnly(lastUpdatedAt)}
        </div>
      </div>

      {resultLimitReached && (
        <div
          style={{
            backgroundColor: "#fffbeb",
            border: "1px solid #fde68a",
            color: "#92400e",
            borderRadius: "8px",
            padding: "9px 11px",
            fontSize: "0.88rem",
          }}
        >
          Showing the latest {ACTIVITY_RESULT_LIMIT.toLocaleString("en-GB")} events.
          Narrow the camera, time or status filter for complete window totals.
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ color: "#555", padding: "8px 4px" }}>
          No recent ingest activity found.
        </div>
      ) : (
        <div
          style={{
            overflowX: isMobile ? "hidden" : "auto",
            overflowY: isMobile ? "auto" : "visible",
            height: isMobile ? `${mobileCardHeight}px` : "auto",
            maxHeight: isMobile ? `${mobileCardHeight}px` : "none",
            minHeight: isMobile ? `${mobileCardHeight}px` : 0,
            border: "1px solid #ececec",
            borderRadius: "10px",
            backgroundColor: "#fff",
            scrollSnapType: isMobile ? "y mandatory" : "none",
            overscrollBehavior: isMobile ? "contain" : "auto",
            WebkitOverflowScrolling: isMobile ? "touch" : undefined,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: isMobile ? "separate" : "collapse",
              borderSpacing: 0,
              fontSize: "0.92rem",
              display: isMobile ? "block" : "table",
            }}
          >
            <thead style={{ display: isMobile ? "none" : "table-header-group" }}>
              <tr style={{ backgroundColor: "#f7f7f7", textAlign: "left" }}>
                <th style={{ padding: "10px 12px" }}>Received</th>
                <th style={{ padding: "10px 12px" }}>Device</th>
                <th style={{ padding: "10px 12px" }}>Job</th>
                <th style={{ padding: "10px 12px" }}>Status</th>
                <th style={{ padding: "10px 12px" }}>Decodes</th>
                <th style={{ padding: "10px 12px" }}>Inserted</th>
                <th style={{ padding: "10px 12px" }}>Duplicates</th>
                <th style={{ padding: "10px 12px" }}>Error</th>
              </tr>
            </thead>

            <tbody style={{ display: isMobile ? "block" : "table-row-group" }}>
              {rows.map((row, index) => (
                <tr
                  key={row.id}
                  ref={index === 0 ? firstEventRowRef : null}
                  style={{
                    borderTop: isMobile ? "none" : "1px solid #efefef",
                    display: isMobile ? "block" : "table-row",
                    width: isMobile ? "100%" : undefined,
                    minWidth: 0,
                    backgroundColor: "#fff",
                    scrollSnapAlign: isMobile ? "start" : "none",
                    scrollSnapStop: isMobile ? "always" : "normal",
                  }}
                >
                  <td style={mobileCellStyle()}>
                    {isMobile && <div style={mobileLabelStyle}>Received</div>}
                    <div
                      style={{
                        minWidth: 0,
                        overflowWrap: "anywhere",
                        whiteSpace: "normal",
                      }}
                    >
                      {formatDateTime(row.received_at)}
                    </div>
                  </td>

                  <td style={mobileCellStyle()}>
                    {isMobile && <div style={mobileLabelStyle}>Device</div>}
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          color: theme.accent_color || "#000",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {row.camera_name ||
                          (row.camera_device_id
                            ? `Camera ${row.camera_device_id}`
                            : "Unknown")}
                      </div>
                      <div
                        style={{
                          color: "#666",
                          fontSize: "0.85rem",
                          overflowWrap: "anywhere",
                        }}
                      >
                        Event #{row.id}
                      </div>
                    </div>
                  </td>

                  <td style={mobileCellStyle()}>
                    {isMobile && <div style={mobileLabelStyle}>Job</div>}
                    <div>{row.job_id ? `#${row.job_id}` : "—"}</div>
                  </td>

                  <td style={mobileCellStyle()}>
                    {isMobile && <div style={mobileLabelStyle}>Status</div>}
                    <div>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 10px",
                          borderRadius: "999px",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                          ...getStatusPillStyle(row.parse_status),
                        }}
                      >
                        {row.parse_status || "unknown"}
                      </span>
                    </div>
                  </td>

                  <td style={mobileCellStyle()}>
                    {isMobile && <div style={mobileLabelStyle}>Decodes</div>}
                    <div>{row.decode_count ?? 0}</div>
                  </td>

                  <td style={mobileCellStyle()}>
                    {isMobile && <div style={mobileLabelStyle}>Inserted</div>}
                    <div>{row.inserted_count ?? 0}</div>
                  </td>

                  <td style={mobileCellStyle()}>
                    {isMobile && (
                      <div style={mobileLabelStyle}>Duplicates</div>
                    )}
                    <div>{row.duplicate_count ?? 0}</div>
                  </td>

                  <td
                    style={{
                      ...mobileCellStyle(true),
                      color: row.parse_error ? "#991b1b" : "#666",
                    }}
                  >
                    {isMobile && <div style={mobileLabelStyle}>Error</div>}
                    <div
                      style={{
                        minWidth: 0,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {row.parse_error || "—"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CameraIngestActivityWidget;
